import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { MemberLookup } from '@/pages/MemberLookup';
import { EligibleMembers } from '@/pages/EligibleMembers';
import { Pickups } from '@/pages/Pickups';
import { StyleGuide } from '@/pages/StyleGuide';
import { getMe } from '@/lib/pickups-api';
import { useEntraIdentity } from '@/hooks/useEntraIdentity';
import type { MeResult } from '@/lib/pickups-types';

/**
 * The authenticated app body. Behind AuthRoute. Owns the single useState page switch (the starter
 * kit's activeId/onNavigate pattern - no nested router inside the shell), fetches GET /api/pickups/me
 * for the admin gate + user chrome, and threads the Eligible Members -> Member Lookup deep-link.
 */

type PageId = 'member' | 'eligible' | 'pickups' | 'styleguide';

const ELIGIBILITY_CACHE_DATE = '2026-06-26';

export function PickupsApp() {
  const [activeId, setActiveId] = useState<PageId>('member');
  const [me, setMe] = useState<MeResult | null>(null);
  const [meLoading, setMeLoading] = useState(true);
  const [meError, setMeError] = useState<string | null>(null);

  // Deep-link target: Eligible Members row click -> Member Lookup.
  const [lookupId, setLookupId] = useState<string | undefined>(undefined);

  const identity = useEntraIdentity();

  useEffect(() => {
    let alive = true;
    getMe()
      .then((res) => {
        if (alive) {
          setMe(res);
          setMeError(null);
        }
      })
      .catch((e) => {
        if (alive) setMeError(`Could not load your identity (HTTP ${(e as { status?: number }).status ?? '?'}).`);
      })
      .finally(() => {
        if (alive) setMeLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  function openMember(memberId: string) {
    setLookupId(memberId);
    setActiveId('member');
  }

  const isAdmin = me?.isAdmin === true;
  const userName = me?.name || identity?.name || 'Signed in';
  const userInitials = identity?.initials || initialsOf(userName);
  const userRole = isAdmin ? 'Admin' : 'Operator';

  const navGroups = [
    {
      label: 'Pickups',
      items: [
        { id: 'member', num: '01', name: 'Member Lookup' },
        { id: 'eligible', num: '02', name: 'Eligible Members' },
        { id: 'pickups', num: '03', name: 'Pickups' },
      ],
    },
    {
      label: 'System',
      items: [{ id: 'styleguide', num: '04', name: 'Style Guide', meta: 'tokens' }],
    },
  ];

  return (
    <AppShell
      brand={{ mark: 'CP', name: 'CDX', nameEm: 'Pickups' }}
      meta={[
        { label: 'Tenant', value: 'Alignment' },
        { label: 'Program', value: '26 FOBT' },
      ]}
      user={{ name: userName, role: userRole, initials: userInitials }}
      freshness={{ state: 'fresh', label: `Eligibility cache: ${ELIGIBILITY_CACHE_DATE}` }}
      navGroups={navGroups}
      activeId={activeId}
      onNavigate={(id) => setActiveId(id as PageId)}
    >
      {meLoading ? (
        <div className="page-pad">
          <LoadingSpinner label="Loading your identity…" />
        </div>
      ) : (
        <>
          {meError && (
            <div className="page-pad" style={{ paddingBottom: 0 }}>
              <div className="note warn">
                <b>Heads up.</b> {meError} Actions that require admin are hidden until this resolves.
              </div>
            </div>
          )}
          {activeId === 'member' && (
            <MemberLookup
              isAdmin={isAdmin}
              initialId={lookupId}
              onConsumeInitialId={() => setLookupId(undefined)}
            />
          )}
          {activeId === 'eligible' && <EligibleMembers onOpenMember={openMember} />}
          {activeId === 'pickups' && <Pickups isAdmin={isAdmin} />}
          {activeId === 'styleguide' && <StyleGuide />}
        </>
      )}
    </AppShell>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
